import React from 'react'
import { useMediaQuery } from '@material-ui/core'
import { MODAL_SCROLL_CLASS } from '../components/PacketModalShell'
import { dark, radius, type as typo } from '../tokens'

const COLLAPSED_WIDTH = 44
const EXPANDED_WIDTH = 360

/**
 * The narrowest window that can hold the modal with this column expanded. Below
 * it the dialog is capped by the viewport instead, and expanding would squeeze
 * the copy column past the point of being usable — so the column takes itself
 * out rather than offering a control that makes the modal worse.
 */
const MIN_VIEWPORT = 1300

/**
 * Whether there is room for the history column at all. Exported because the
 * modal sizes its dialog from the regions present, and an absent column should
 * not leave a reserved gap.
 */
export function useHistoryFits(): boolean {
  // `noSsr` so the first render already knows: a comment link decides from it
  // whether the drawer has to open.
  return useMediaQuery(`(min-width:${MIN_VIEWPORT}px)`, { noSsr: true })
}

/**
 * The ad's history and comments, as a column beside the copy rather than under
 * it — this surface has spare width and no spare height — and never as an
 * overlay, which would cover the field the reviewer opened it to fix.
 *
 * It owns both its widths and its control so the modal only has to hand it a
 * grid cell. Open state belongs to the page, not here: the modal is remounted
 * per ad, so state held below that point would reset on every prev/next.
 */
export function ReviewHistory({
  open,
  onToggle,
  children,
}: {
  open: boolean
  onToggle: () => void
  children?: React.ReactNode
}) {
  const fits = useHistoryFits()
  const [hover, setHover] = React.useState(false)
  if (!fits) return null

  return (
    <div
      style={{
        width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        borderLeft: `1px solid ${dark.hairline}`,
        paddingLeft: 12,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={open ? 'Hide history' : 'Show history'}
        aria-expanded={open}
        aria-controls="review-history-panel"
        title={open ? 'Hide history' : 'Show history'}
        style={{
          alignSelf: open ? 'flex-start' : 'center',
          flexShrink: 0,
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          border: `1px solid ${hover || open ? dark.accent : dark.hairline}`,
          background: open ? dark.accent : hover ? dark.hairline : dark.bg,
          color: open ? dark.accentInk : dark.text,
          cursor: 'pointer',
          transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        }}
      >
        <i className="fa fa-history" style={{ color: 'inherit' }} aria-hidden="true" />
      </button>

      <div
        id="review-history-panel"
        hidden={!open}
        className={MODAL_SCROLL_CLASS}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          fontFamily: typo.family.sans,
          fontSize: typo.size.body,
          color: dark.text,
          borderRadius: radius.md,
        }}
      >
        {children}
      </div>
    </div>
  )
}
